import { Button } from "@nimit9/signet-ui/components/button"
import { Input } from "@nimit9/signet-ui/components/input"
import { StatStrip } from "@nimit9/signet-ui/components/stat-strip"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import { createFileRoute } from "@tanstack/react-router"
import { Plus, X } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { Amount } from "@/components/common/amount"
import { useInvestments } from "@/hooks/use-investments"
import { cn } from "@/lib/cn"
import { type CashflowRow, computeXirrSummary } from "./-compute"
import { buildXirrHeadline } from "./-headline"
import { CARD, CalculatorLayout } from "./-layout"

export const Route = createFileRoute("/calculators/xirr")({
  component: XIRRCalculator,
})

const ROW_GRID = "grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-2"

function XIRRCalculator() {
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const today = new Date().toISOString().split("T")[0]
  const { data: investData } = useInvestments()
  const [prefilled, setPrefilled] = useState(false)
  const [rows, setRows] = useState<CashflowRow[]>([
    { id: 1, date: "2022-01-15", amount: "-100000" },
    { id: 2, date: "2023-01-15", amount: "-50000" },
    { id: 3, date: today, amount: "175000" },
  ])

  const investApplied = useRef(false)
  useEffect(() => {
    if (investApplied.current || !investData) return
    const sipsWithData = investData.sips.all.filter(
      (s) => s.totalInvested && parseFloat(s.totalInvested) > 0,
    )
    if (!sipsWithData.length) return
    investApplied.current = true
    const sipRows: CashflowRow[] = sipsWithData.map((s, i) => ({
      id: i + 1,
      date: s.startDate,
      amount: `-${Math.round(parseFloat(s.totalInvested ?? "0"))}`,
    }))
    const currentVal = Math.round(investData.summary.totalCurrent)
    if (currentVal > 0) {
      sipRows.push({ id: sipRows.length + 1, date: today, amount: String(currentVal) })
    }
    setRows(sipRows)
    setPrefilled(true)
  }, [investData, today])

  const addRow = () => {
    // Next id from the rows themselves (not a module-level counter, which
    // leaked across mounts and could collide with pre-filled ids).
    setRows((prev) => [
      ...prev,
      { id: Math.max(0, ...prev.map((r) => r.id)) + 1, date: today, amount: "" },
    ])
  }
  const removeRow = (id: number) => setRows((prev) => prev.filter((r) => r.id !== id))
  const updateRow = (id: number, field: keyof Omit<CashflowRow, "id">, value: string) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)))
  }

  const summary = useMemo(() => computeXirrSummary(rows), [rows])
  const { xirr, totalInvested, currentValue, gain } = summary
  const headline = buildXirrHeadline(summary)

  return (
    <CalculatorLayout
      title="XIRR Calculator"
      headline={headline}
      isDesktop={isDesktop}
      inputsLabel="Cashflows"
      inputsWidth="wide"
      prefilledNote={prefilled ? "Pre-filled from your portfolio" : null}
      inputs={
        <>
          <div className="flex flex-col gap-1">
            <h2 className="text-callout font-semibold text-text-primary">Cashflows</h2>
            <p className="text-caption text-text-muted">
              Use negative amounts for investments, positive for redemptions / current value
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <div className={ROW_GRID} aria-hidden>
              <p className="text-caption text-text-muted">Date</p>
              <p className="text-caption text-text-muted">Amount (₹)</p>
              <span className="size-8" />
            </div>
            {rows.map((row, i) => (
              <div key={row.id} data-testid="cashflow-row" className={ROW_GRID}>
                <Input
                  type="date"
                  aria-label={`Cashflow ${i + 1} date`}
                  value={row.date}
                  onChange={(e) => updateRow(row.id, "date", e.target.value)}
                />
                <Input
                  type="number"
                  inputMode="decimal"
                  aria-label={`Cashflow ${i + 1} amount`}
                  placeholder="-100000"
                  value={row.amount}
                  onChange={(e) => updateRow(row.id, "amount", e.target.value)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove cashflow ${i + 1}`}
                  onClick={() => removeRow(row.id)}
                  disabled={rows.length <= 2}
                >
                  <X className="size-4" />
                </Button>
              </div>
            ))}
          </div>

          <Button type="button" variant="outline" className="self-start" onClick={addRow}>
            <Plus className="size-4" />
            Add row
          </Button>

          <p className="text-caption text-text-hint">
            Tip: Add all purchase dates with negative amounts, then add today's date with the
            current portfolio value as a positive amount.
          </p>
        </>
      }
    >
      <section
        data-testid="xirr-result"
        aria-label="XIRR"
        className={cn(CARD, "flex flex-col gap-1 p-4 md:p-5")}
      >
        <p className="text-caption text-text-muted">XIRR</p>
        {xirr !== null ? (
          <>
            <p
              className={cn(
                "font-mono text-display font-semibold tracking-tight tabular-nums",
                xirr >= 0 ? "text-money-credit" : "text-accent-amber",
              )}
            >
              {(xirr * 100).toFixed(2)}%
            </p>
            <p className="text-caption text-text-muted">annualised return</p>
          </>
        ) : (
          <p className="text-callout text-text-muted">
            Need at least one positive and one negative cashflow
          </p>
        )}
      </section>

      <StatStrip
        isDesktop={false}
        items={[
          { label: "Total invested", value: <Amount value={totalInvested} /> },
          { label: "Current value", value: <Amount value={currentValue} /> },
          {
            label: "Gain / loss",
            value: <Amount value={gain} showSign />,
            tone: gain < 0 ? "warn" : "good",
            meta: gain < 0 ? "below what you put in" : undefined,
          },
        ]}
      />

      <section className={cn(CARD, "flex flex-col gap-2 p-4")}>
        <h2 className="text-caption text-text-muted">What is XIRR?</h2>
        <p className="text-body leading-relaxed text-text-secondary">
          XIRR (Extended IRR) is the annualised return accounting for the exact dates of each
          cashflow — unlike simple returns which ignore timing. It's the industry standard for
          measuring mutual fund and SIP performance.
        </p>
      </section>
    </CalculatorLayout>
  )
}
